import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import { CopyOutlined, DeleteOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SetPageHeader } from '@actions/userActions';
import { useAppDispatch, useAppSelector } from '@config/reduxStore';
import { ApiConsumer, ApiConsumerService, CreateApiConsumerRequest } from '@models/ApiConsumer';
import {
  ALL_API_CONSUMER_SERVICES,
  API_CONSUMER_SERVICES,
  canManageApiConsumers,
  createApiConsumer,
  getApiConsumers,
  revokeApiConsumer,
} from '@services/apiConsumers';

const { Paragraph, Text } = Typography;

type ApiConsumerFormValues = Omit<CreateApiConsumerRequest, 'expiresInDays'> & {
  expiresInDays: 30 | 60 | 90 | 'never';
};

function formatDate(value?: string | null) {
  if (!value) return 'Nunca';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

export function ApiConsumersView() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ApiConsumerFormValues>();
  const [createOpen, setCreateOpen] = useState(false);
  const [issuedToken, setIssuedToken] = useState<{ name: string; token: string } | null>(null);
  const hasAccess = canManageApiConsumers(user);

  useEffect(() => {
    dispatch(SetPageHeader({
      title: 'Consumidores da API',
      subtitle: 'Integrações externas',
      backbutton: true,
      extras: [],
      menu: 'consumidoresapi',
    }));
  }, [dispatch]);

  const consumersQuery = useQuery(['api-consumers'], getApiConsumers, { enabled: hasAccess, retry: 1 });

  const createMutation = useMutation(createApiConsumer, {
    onSuccess: ({ consumer, accessToken }) => {
      setCreateOpen(false);
      setIssuedToken({ name: consumer.name, token: accessToken });
      form.resetFields();
      queryClient.invalidateQueries(['api-consumers']);
    },
    onError: () => notification.error({ message: 'Não foi possível cadastrar o consumidor.' }),
  });

  const revokeMutation = useMutation(revokeApiConsumer, {
    onSuccess: () => {
      notification.success({ message: 'Acesso do consumidor revogado.' });
      queryClient.invalidateQueries(['api-consumers']);
    },
    onError: () => notification.error({ message: 'Não foi possível revogar o consumidor.' }),
  });

  const consumers = useMemo(() => consumersQuery.data || [], [consumersQuery.data]);

  if (!hasAccess) {
    return <Result status="403" title="Área restrita" subTitle="Somente administradores podem gerenciar consumidores da API." />;
  }

  function confirmRevoke(consumer: ApiConsumer) {
    Modal.confirm({
      title: `Revogar acesso de ${consumer.name}?`,
      content: 'O token deixará de funcionar imediatamente. Esta ação não pode ser desfeita.',
      okText: 'Revogar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => revokeMutation.mutateAsync(consumer.id),
    });
  }

  async function copyToken() {
    if (!issuedToken) return;
    await navigator.clipboard.writeText(issuedToken.token);
    notification.success({ message: 'Token copiado.' });
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          showIcon
          type="warning"
          message="Tokens concedem acesso direto aos dados selecionados"
          description="Crie um consumidor por sistema, aplique o menor privilégio possível e revogue imediatamente tokens comprometidos. O token completo é exibido uma única vez."
        />

        <Card
          size="small"
          title="Consumidores cadastrados"
          extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Novo consumidor</Button>}
        >
          {consumersQuery.isError && (
            <Alert type="error" showIcon message="Não foi possível carregar os consumidores." style={{ marginBottom: 16 }} />
          )}
          <Table<ApiConsumer>
            rowKey="id"
            loading={consumersQuery.isLoading}
            dataSource={consumers}
            locale={{ emptyText: <Empty description="Nenhum consumidor cadastrado" /> }}
            scroll={{ x: 900 }}
          >
            <Table.Column<ApiConsumer> title="Nome" dataIndex="name" fixed="left" />
            <Table.Column<ApiConsumer>
              title="Serviços"
              dataIndex="services"
              render={(services: ApiConsumerService[]) => <Space size={[4, 4]} wrap>{services.map((service) => <Tag key={service}>{API_CONSUMER_SERVICES[service]?.label || service}</Tag>)}</Space>}
            />
            <Table.Column<ApiConsumer> title="Criado em" dataIndex="createdAt" render={formatDate} />
            <Table.Column<ApiConsumer> title="Expira em" dataIndex="expiresAt" render={formatDate} />
            <Table.Column<ApiConsumer> title="Último uso" dataIndex="lastUsedAt" render={formatDate} />
            <Table.Column<ApiConsumer>
              title="Status"
              dataIndex="status"
              render={(status) => <Tag color={status === 'active' ? 'green' : status === 'expired' ? 'orange' : 'default'}>{status === 'active' ? 'Ativo' : status === 'expired' ? 'Expirado' : 'Revogado'}</Tag>}
            />
            <Table.Column<ApiConsumer>
              width={90}
              render={(_, consumer) => <Button danger size="small" icon={<DeleteOutlined />} aria-label={`Revogar ${consumer.name}`} disabled={consumer.status !== 'active'} onClick={() => confirmRevoke(consumer)} />}
            />
          </Table>
        </Card>
      </Space>

      <Modal
        title="Novo consumidor da API"
        open={createOpen}
        okText="Gerar access token"
        cancelText="Cancelar"
        confirmLoading={createMutation.isLoading}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => createMutation.mutate({
            ...values,
            expiresInDays: values.expiresInDays === 'never' ? null : values.expiresInDays,
          })}
          initialValues={{ expiresInDays: 30 }}
        >
          <Form.Item name="name" label="Nome do consumidor" rules={[{ required: true, whitespace: true, message: 'Informe um nome.' }, { max: 100, message: 'Use no máximo 100 caracteres.' }]}>
            <Input placeholder="Ex.: BI Financeiro" autoComplete="off" />
          </Form.Item>
          <Form.Item name="services" label="Serviços permitidos" rules={[{ required: true, message: 'Selecione ao menos um serviço.' }]}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Space direction="vertical">
                {ALL_API_CONSUMER_SERVICES.map((service) => (
                  <Checkbox key={service} value={service}>
                    <Text strong>{API_CONSUMER_SERVICES[service].label}</Text>
                    <br />
                    <Text type="secondary">{API_CONSUMER_SERVICES[service].description}</Text>
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="expiresInDays" label="Validade do token" rules={[{ required: true, message: 'Selecione a validade.' }]}>
            <Select options={[{ value: 30, label: '30 dias' }, { value: 60, label: '60 dias' }, { value: 90, label: '90 dias' }, { value: 'never', label: 'Nunca' }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<Space><KeyOutlined />Access token gerado</Space>}
        open={!!issuedToken}
        okText="Copiar token"
        cancelText="Já salvei o token"
        onOk={copyToken}
        onCancel={() => setIssuedToken(null)}
        closable={false}
        maskClosable={false}
      >
        <Alert type="success" showIcon message={`Consumidor ${issuedToken?.name || ''} cadastrado`} description="Copie e armazene o token agora. Por segurança, ele não será exibido novamente." />
        <Paragraph copyable={false} code style={{ marginTop: 16, padding: 12, wordBreak: 'break-all' }}>{issuedToken?.token}</Paragraph>
        <Text type="secondary">Use em todas as requisições: <Text code>Authorization: Bearer SEU_ACCESS_TOKEN</Text></Text>
        <Button block icon={<CopyOutlined />} onClick={copyToken} style={{ marginTop: 16 }}>Copiar access token</Button>
      </Modal>
    </div>
  );
}

export default ApiConsumersView;
